if (!customElements.get('foodchain-product-tabs')) {
  customElements.define(
    'foodchain-product-tabs',
    class FoodchainProductTabs extends HTMLElement {
      constructor() {
        super();
        this.handleClick = this.handleClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleBlockSelect = this.handleBlockSelect.bind(this);
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.refreshDom();
        this.bindEvents();
        this.setActiveTab(this.getInitialIndex());

        if (window.Shopify && window.Shopify.designMode) {
          document.addEventListener('shopify:block:select', this.handleBlockSelect);
        }
      }

      disconnectedCallback() {
        this.unbindEvents();
        document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      }

      refreshDom() {
        this.tabs = Array.from(this.querySelectorAll('[data-foodchain-product-tabs-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-foodchain-product-tabs-panel]'));
      }

      bindEvents() {
        this.unbindEvents();
        this.tabs.forEach((tab) => {
          tab.addEventListener('click', this.handleClick);
          tab.addEventListener('keydown', this.handleKeydown);
        });
      }

      unbindEvents() {
        if (!this.tabs) return;

        this.tabs.forEach((tab) => {
          tab.removeEventListener('click', this.handleClick);
          tab.removeEventListener('keydown', this.handleKeydown);
        });
      }

      getInitialIndex() {
        const selectedIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
        return selectedIndex > -1 ? selectedIndex : 0;
      }

      getTabIndex(tab) {
        const index = Number(tab.dataset.foodchainTabIndex);
        return Number.isNaN(index) ? 0 : index;
      }

      handleClick(event) {
        event.preventDefault();
        this.setActiveTab(this.getTabIndex(event.currentTarget));
      }

      handleKeydown(event) {
        const currentIndex = this.getTabIndex(event.currentTarget);
        const isRtl = document.documentElement.dir === 'rtl';
        let nextIndex = currentIndex;

        switch (event.key) {
          case 'ArrowRight':
            nextIndex = isRtl ? currentIndex - 1 : currentIndex + 1;
            break;
          case 'ArrowLeft':
            nextIndex = isRtl ? currentIndex + 1 : currentIndex - 1;
            break;
          case 'Home':
            nextIndex = 0;
            break;
          case 'End':
            nextIndex = this.tabs.length - 1;
            break;
          case 'Enter':
          case ' ':
          case 'Spacebar':
            event.preventDefault();
            this.setActiveTab(currentIndex);
            return;
          default:
            return;
        }

        event.preventDefault();
        this.setActiveTab(this.normalizeIndex(nextIndex), true);
      }

      handleBlockSelect(event) {
        if (!event.detail || event.detail.sectionId !== this.sectionId || !(event.target instanceof Element)) return;

        const selectedTab = event.target.closest('[data-foodchain-product-tabs-tab]');
        if (selectedTab && this.contains(selectedTab)) {
          this.setActiveTab(this.getTabIndex(selectedTab), true);
          return;
        }

        const selectedPanel = event.target.closest('[data-foodchain-product-tabs-panel]');
        if (!selectedPanel || !this.contains(selectedPanel)) return;

        this.setActiveTab(Number(selectedPanel.dataset.foodchainTabIndex));

        const accordion = event.target.closest('.foodchain-product-tabs__accordion');
        const accordionDetails = accordion && accordion.querySelector('details');
        if (accordionDetails) {
          accordionDetails.open = true;
        }

        const ingredientSlide = event.target.closest('[data-foodchain-ingredient-slide]');
        const ingredientCarousel = ingredientSlide && ingredientSlide.closest('foodchain-ingredient-carousel');
        if (ingredientCarousel && typeof ingredientCarousel.slideToElement === 'function') {
          ingredientCarousel.slideToElement(ingredientSlide);
        }
      }

      normalizeIndex(index) {
        if (!this.tabs.length || Number.isNaN(index)) return 0;
        if (index < 0) return this.tabs.length - 1;
        if (index >= this.tabs.length) return 0;
        return index;
      }

      setActiveTab(index, focusTab = false) {
        if (!this.tabs.length || !this.panels.length) return;

        const activeIndex = this.normalizeIndex(index);
        const selectedTab = this.tabs[activeIndex];
        const selectedPanel = this.panels[activeIndex];

        this.tabs.forEach((tab, tabIndex) => {
          const isActive = tabIndex === activeIndex;
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
          tab.setAttribute('tabindex', isActive ? '0' : '-1');
        });

        this.panels.forEach((panel, panelIndex) => {
          const isActive = panelIndex === activeIndex;
          panel.hidden = false;
          panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');

          if (isActive) {
            panel.removeAttribute('inert');
          } else {
            panel.setAttribute('inert', '');
          }
        });

        if (focusTab && selectedTab) {
          selectedTab.focus({ preventScroll: true });
          this.scrollTabIntoView(selectedTab);
        }

        this.refreshIngredientCarousels(selectedPanel);
        this.refreshAppBlocks(selectedPanel);

        this.dispatchEvent(
          new CustomEvent('tabChange', {
            bubbles: true,
            detail: { selectedIndex: activeIndex, selectedTab: selectedPanel }
          })
        );
      }

      scrollTabIntoView(tab) {
        const nav = tab.closest('[data-foodchain-product-tabs-nav]');
        if (!nav) return;

        const behavior =
          window.FoxTheme && window.FoxTheme.config && window.FoxTheme.config.motionReduced ? 'auto' : 'smooth';

        tab.scrollIntoView({
          behavior,
          block: 'nearest',
          inline: 'center'
        });
      }

      refreshIngredientCarousels(panel) {
        if (!panel) return;

        panel.querySelectorAll('foodchain-ingredient-carousel').forEach((carousel) => {
          if (typeof carousel.refreshSlider === 'function') {
            carousel.refreshSlider();
          }
        });
      }

      refreshAppBlocks(panel) {
        if (
          !panel ||
          !panel.querySelector('[data-foodchain-product-tabs-app], .jdgm-widget, [class*="jdgm-"], [id*="judgeme"]')
        ) {
          return;
        }

        const refresh = () => {
          window.dispatchEvent(new Event('resize'));
          this.refreshJudgeMe(panel);
        };

        window.requestAnimationFrame(() => {
          refresh();
          window.setTimeout(refresh, 150);
        });
      }

      refreshJudgeMe(panel) {
        if (!panel.querySelector('.jdgm-widget, [class*="jdgm-"], [id*="judgeme"]')) return;

        const judgeMe = window.jdgm;
        if (!judgeMe) return;

        ['initialize', 'loadBadges', 'customizeBadges', 'renderWidgets', 'refresh'].forEach((method) => {
          if (typeof judgeMe[method] !== 'function') return;

          try {
            judgeMe[method]();
          } catch (error) {
            // Third-party widgets can expose different method signatures across versions.
          }
        });
      }
    }
  );
}

if (!customElements.get('foodchain-ingredient-carousel')) {
  customElements.define(
    'foodchain-ingredient-carousel',
    class FoodchainIngredientCarousel extends HTMLElement {
      constructor() {
        super();
        this.handlePreviousClick = this.handlePreviousClick.bind(this);
        this.handleNextClick = this.handleNextClick.bind(this);
      }

      connectedCallback() {
        this.sliderWrapper = this.querySelector('[data-foodchain-ingredient-track]');
        this.previousButton = this.querySelector('.swiper-button-prev');
        this.nextButton = this.querySelector('.swiper-button-next');
        this.enableSlider = this.dataset.enableSlider === 'true';
        this.items = parseInt(this.dataset.items, 10) || 4;
        this.mobileItems = parseInt(this.dataset.mobileItems, 10) || 2;
        this.sliderInstance = false;

        if (!this.enableSlider || !this.sliderWrapper) return;

        this.bindEvents();

        window.requestAnimationFrame(() => {
          this.initSlider();
        });
      }

      disconnectedCallback() {
        this.unbindEvents();
        this.destroySlider();
      }

      bindEvents() {
        this.unbindEvents();
        this.previousButton && this.previousButton.addEventListener('click', this.handlePreviousClick);
        this.nextButton && this.nextButton.addEventListener('click', this.handleNextClick);
      }

      unbindEvents() {
        this.previousButton && this.previousButton.removeEventListener('click', this.handlePreviousClick);
        this.nextButton && this.nextButton.removeEventListener('click', this.handleNextClick);
      }

      initSlider() {
        if (typeof this.sliderInstance === 'object') return;
        if (!window.FoxTheme || !window.FoxTheme.Carousel || !window.FoxTheme.Swiper) return;

        const pagination = this.querySelector('.swiper-pagination');
        const desktopItems = Math.max(this.items, this.mobileItems);
        const tabletItems = Math.min(desktopItems, 3);

        this.sliderOptions = {
          slidesPerView: this.mobileItems,
          spaceBetween: this.getSpaceBetween(),
          navigation: false,
          pagination: pagination
            ? {
                el: pagination,
                type: 'bullets',
                clickable: true
              }
            : false,
          breakpoints: {
            768: {
              slidesPerView: tabletItems
            },
            1024: {
              slidesPerView: desktopItems
            }
          },
          loop: false,
          speed: 450,
          threshold: 2,
          watchSlidesProgress: true,
          mousewheel: {
            enabled: true,
            forceToAxis: true
          },
          observer: true,
          observeParents: true,
          on: {
            init: () => this.updateControls(),
            slideChange: () => {
              this.ensureVisibleActiveSlide();
              this.updateControls();
            },
            resize: () => {
              this.ensureVisibleActiveSlide();
              this.updateControls();
            },
            update: () => {
              this.ensureVisibleActiveSlide();
              this.updateControls();
            }
          }
        };

        this.sliderInstance = new window.FoxTheme.Carousel(this, this.sliderOptions, [FoxTheme.Swiper.Mousewheel]);
        this.sliderInstance.init();
        this.updateControls();
      }

      destroySlider() {
        if (typeof this.sliderInstance !== 'object') return;

        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;
      }

      refreshSlider() {
        if (!this.enableSlider) return;

        if (typeof this.sliderInstance !== 'object') {
          this.initSlider();
          return;
        }

        window.requestAnimationFrame(() => {
          this.sliderInstance.slider.params.spaceBetween = this.getSpaceBetween();
          this.sliderInstance.slider.update();
          this.ensureVisibleActiveSlide();
          this.updateControls();
        });
      }

      handlePreviousClick(event) {
        event.preventDefault();
        this.slide('previous');
      }

      handleNextClick(event) {
        event.preventDefault();
        this.slide('next');
      }

      slide(direction) {
        this.refreshSlider();

        if (typeof this.sliderInstance !== 'object') return;

        const visibleSlides = this.getVisibleSlides();
        const currentVisibleIndex = this.getActiveVisibleIndex(visibleSlides);

        if (currentVisibleIndex < 0) return;

        const nextVisibleIndex =
          direction === 'previous'
            ? Math.max(currentVisibleIndex - 1, 0)
            : Math.min(currentVisibleIndex + 1, visibleSlides.length - 1);

        const slides = Array.from(this.sliderInstance.slider.slides);
        const targetSlide = visibleSlides[nextVisibleIndex];
        const targetIndex = slides.indexOf(targetSlide);

        if (targetIndex > -1) {
          this.sliderInstance.slider.slideTo(targetIndex);
          this.updateControls();
          return;
        }

        if (direction === 'previous') {
          this.sliderInstance.slider.slidePrev();
        } else {
          this.sliderInstance.slider.slideNext();
        }

        this.updateControls();
      }

      slideToElement(slide) {
        if (!slide) return;

        this.refreshSlider();

        if (typeof this.sliderInstance !== 'object') return;

        const slides = Array.from(this.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)'));
        const slideIndex = slides.indexOf(slide);
        if (slideIndex > -1) {
          this.sliderInstance.slider.slideTo(slideIndex);
        }
      }

      updateControls() {
        if (
          typeof this.sliderInstance !== 'object' ||
          !this.sliderInstance.slider ||
          !this.previousButton ||
          !this.nextButton
        ) {
          return;
        }

        const slider = this.sliderInstance.slider;
        const visibleSlides = this.getVisibleSlides();
        const currentVisibleIndex = this.getActiveVisibleIndex(visibleSlides);
        const slidesPerView = this.getCurrentSlidesPerView();
        const disableControls = visibleSlides.length <= slidesPerView || currentVisibleIndex < 0;

        this.updatePaginationVisibility(visibleSlides);
        this.updateButtonState(this.previousButton, disableControls || currentVisibleIndex <= 0 || slider.isBeginning);
        this.updateButtonState(
          this.nextButton,
          disableControls || currentVisibleIndex >= visibleSlides.length - slidesPerView || slider.isEnd
        );
      }

      updateButtonState(button, isDisabled) {
        button.disabled = isDisabled;
        button.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
        button.classList.toggle('swiper-button-disabled', isDisabled);
      }

      getSpaceBetween() {
        const value = window
          .getComputedStyle(this)
          .getPropertyValue('--foodchain-product-tabs-ingredient-gap')
          .trim();

        return this.parseCssLength(value, 16);
      }

      parseCssLength(value, fallback) {
        if (!value) return fallback;

        const numericValue = parseFloat(value);
        if (Number.isNaN(numericValue)) return fallback;
        if (value.endsWith('rem')) {
          return numericValue * parseFloat(window.getComputedStyle(document.documentElement).fontSize);
        }

        return numericValue;
      }

      getCurrentSlidesPerView() {
        if (window.matchMedia('(min-width: 1024px)').matches) return Math.max(this.items, this.mobileItems);
        if (window.matchMedia('(min-width: 768px)').matches) return Math.min(Math.max(this.items, this.mobileItems), 3);
        return this.mobileItems;
      }

      getVisibleSlides() {
        if (typeof this.sliderInstance !== 'object' || !this.sliderInstance.slider) return [];

        return Array.from(this.sliderInstance.slider.slides).filter((slide) => {
          return !slide.classList.contains('swiper-slide-duplicate') && this.isSlideVisible(slide);
        });
      }

      getActiveVisibleIndex(visibleSlides) {
        if (!visibleSlides.length || typeof this.sliderInstance !== 'object') return -1;

        const slides = Array.from(this.sliderInstance.slider.slides);
        const activeSlide = slides[this.sliderInstance.slider.activeIndex];
        const directIndex = visibleSlides.indexOf(activeSlide);
        if (directIndex > -1) return directIndex;

        const activeIndex = this.sliderInstance.slider.activeIndex;
        const nextVisibleIndex = visibleSlides.findIndex((slide) => slides.indexOf(slide) > activeIndex);
        return nextVisibleIndex > -1 ? nextVisibleIndex : visibleSlides.length - 1;
      }

      ensureVisibleActiveSlide() {
        if (typeof this.sliderInstance !== 'object' || !this.sliderInstance.slider) return;

        const visibleSlides = this.getVisibleSlides();
        if (!visibleSlides.length) return;

        const slides = Array.from(this.sliderInstance.slider.slides);
        const activeSlide = slides[this.sliderInstance.slider.activeIndex];
        if (visibleSlides.includes(activeSlide)) return;

        const activeIndex = this.sliderInstance.slider.activeIndex;
        const targetSlide = visibleSlides.find((slide) => slides.indexOf(slide) > activeIndex) || visibleSlides[0];
        const targetIndex = slides.indexOf(targetSlide);

        if (targetIndex > -1) {
          this.sliderInstance.slider.slideTo(targetIndex, 0);
        }
      }

      isSlideVisible(slide) {
        return window.getComputedStyle(slide).display !== 'none';
      }

      updatePaginationVisibility(visibleSlides) {
        const pagination = this.querySelector('.swiper-pagination');
        if (!pagination || typeof this.sliderInstance !== 'object') return;

        const visibleSlidesSet = new Set(visibleSlides);
        const slides = Array.from(this.sliderInstance.slider.slides).filter(
          (slide) => !slide.classList.contains('swiper-slide-duplicate')
        );

        pagination.querySelectorAll('.swiper-pagination-bullet').forEach((bullet, index) => {
          const isVisible = visibleSlidesSet.has(slides[index]);
          bullet.hidden = !isVisible;
          bullet.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
        });
      }
    }
  );
}

if (!customElements.get('product-tabs')) {
  customElements.define(
    'product-tabs',
    class ProductTabs extends HTMLDivElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.tabPanels = this.querySelector('.tabs__panels');
        this.tabBtns = this.querySelectorAll('.tabs__btn');
        this.currentIndex = 0;
        this.init();
      }

      init() {
        this.tabBtns.forEach((btn) => {
          btn.addEventListener('click', this.onClick.bind(this));
        });

        if (Shopify.designMode) {
          this.addEventListener('shopify:block:select', (e) => {
            if (e.detail.sectionId != this.sectionId) return;
            let { target } = e;
            const index = Number(target.dataset.index);

            this.setActiveTab(index);
          });
        }
      }

      onClick(e) {
        e.preventDefault();
        const { target } = e;
        const button = target.closest('button');
        const index = Number(button.dataset.index);
        this.setActiveTab(index);
      }

      setActiveTab(index) {
        if (this.currentIndex === index) return;

        let newTab, newBtn, currentItem;

        currentItem = this.tabBtns[this.currentIndex];
        newBtn = this.tabBtns[index];

        currentItem.classList.remove('active');
        newBtn.classList.add('active');

        const tabPanel = this.tabPanels.querySelectorAll('.tabs__panel');
        tabPanel.forEach((panel) => {
          panel.hidden = true;
        });

        newTab = this.tabPanels.querySelector(`.tabs__panel[data-index="${index}"]`);

        if (!newTab) {
          const template = this.tabPanels.querySelector(`template[data-index="${index}"]`);
          newTab = template.content.cloneNode(true).firstElementChild;
          this.tabPanels.appendChild(newTab);
        }
        const translateY = !FoxTheme.a11y.shouldAnimate() ? 0 : 2.5;

        FoxTheme.Motion.animate(newTab, { transform: `translateY(${translateY}rem)`, opacity: 0.01 }, { duration: 0 });

        newTab.hidden = false;
        this.fixBlurryImagesOnSafari(newTab);

        setTimeout(() => {
          FoxTheme.Motion.animate(
            newTab,
            { transform: 'translateY(0)', opacity: 1 },
            { duration: 0.5, delay: this.animationDelay, easing: [0, 0, 0.3, 1] }
          );
        }, 50);

        this.currentIndex = index;

        this.dispatchEvent(
          new CustomEvent('tabChange', {
            bubbles: true,
            detail: { selectedIndex: index, selectedTab: newTab }
          })
        );
      }

      /**
       * Fix for Safari 15.6: force images to reload with correct size after becoming visible
       * @param {*} container
       * @returns
       */
      fixBlurryImagesOnSafari(tab) {
        const isOldSafari =
          /^((?!chrome|android).)*safari/i.test(navigator.userAgent) && navigator.userAgent.includes('Version/15.');

        if (!isOldSafari) return;

        if (tab.classList.contains('images-loaded')) return;
        tab.classList.add('images-loaded');

        const images = tab.querySelectorAll('img[srcset]');
        images.forEach((img) => {
          const src = img.currentSrc || img.src;
          img.srcset = img.srcset; // Trick to trigger evaluation.
          img.src = ''; // Force reload.
          img.src = src;
        });
      }
    },
    { extends: 'div' }
  );
}
